import { useImmer } from "use-immer";
import { useContext, useEffect, useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import {
  useCategorySelectChip,
  usePermission,
  useStatusSelect,
  useStoreSelect,
  useStoreSelectQueries,
} from "../../../shared";
import { useTemplateQueries } from "./useTemplateQueries";
import { useSceduleSelect } from "../../../shared/hooks/useSceduleSelect";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { useTempDelete } from "../../../shared/hooks/useTempDelete";
import { navigateBasedOnUserType } from "../../../utils";
import { AppContext } from "../../../store";

// Utility to remove hasError from all batches
function sanitizeBatchDetails(batchDetails) {
  return batchDetails.map((batch) => ({
    ...batch,
    products: batch.products.map((product) => ({
      ...product,
      batches: product.batches.map(({ hasError, ...rest }) => rest),
    })),
  }));
}

const getOtherRecipes = (otherRecipes = []) => {
  let recipes = [];
  const isNotEmptyArray =
    Array.isArray(otherRecipes) && otherRecipes?.length > 0;
  if (isNotEmptyArray) {
    const isCategory = Array.isArray(otherRecipes[0]?.recipes);
    switch (isCategory) {
      case true:
        recipes = otherRecipes.flatMap((r) => r.recipes);
        break;
      default:
        recipes = otherRecipes;
        break;
    }
  }
  return recipes;
};

export const useTemplates = ({
  list = false,
  details_id,
  dropdownlist = false,
  updateId,
  isList,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [editedMap, setEditedMap] = useState({});
  const [focusedField, setFocusedField] = useState(null); // Track single focused field
  const [, forceUpdate] = useState();
  const [isSearching, setIsSearching] = useState(false);

  // ====================================================================== PERMISSION SECTION ======================================================================
  const isDeletable = usePermission("template-delete");
  const isCreateVisible = usePermission("template-create");
  const isEditable = usePermission("template-modify");
  const isStatusChange = usePermission("template-status_change");
  const isStoreAssign = usePermission("template-assign_store");
  const isCreateSchedule = usePermission("template-shedule");
  const isCopyTemplate = usePermission("template-copy");

  // React hook validator
  const validator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  // Utility hooks
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const { globalData } = useContext(AppContext);
  const navigate = useNavigate();
  const { deleteInfo, onDelete, resetDeleteInfo, onDeleteDetails, templateId } =
    useTempDelete();

  // Template state
  const [templates, setTemplates] = useImmer({
    isTabChange: false,
    allLastBatchWeightZero: false,
    isCreateDialog: false,
    type: "",
    isDeleteBatchDialogOpen: false,
    batchIndex: null,
    batch_template_id: null,
    batchDetails: [],
    pc_temp_id: null,
    isDlt: false,
    template_scheduled_status: 0,
    isDeleteScheduledDatesDialogOpen: false,
    isfilterData: false,
    selectedStores: [],
    is_create_date_assign: 0,
    isStoreDialogOpen: false,
    id: null,
    isCreateStoreAssign: false,
    isCreateFlow: false,
    isCreateDateAssign: false,
    isShowErrorMessage: false,
    errorMessageData: {},
    indexColumn: 0,
    selectedBatch: null,
    selectedRecipe: null,
    templateResultRows: [],
    totalTemplateSearchResult: 0,
    isInitialLoad: false,
    selectedCategory: {},
    groupedDatas: [],
    isRecurringDateDelete: 0,
    isRecurringDialog: false,
    isDeleteDialogOpen: false,
    combinedList: [],
    isDateSaveButton: true,
    chipCategoryId: "",
    selectedDate: "",
    isList: false,
    isChanged: false,
    isTrue: false,
    isRegenerateOpen: false,
    isOpenDialog: false,
    templateItems: {},
    templateBatchItems: {},
    batchesItems: {},
    recipeItems: {},
    templateDetails: {},
    recipeList: [],
    masterRecipeList: [],
    allData: [],
    datas: [],
    updateValue: [],
    filter: {
      search: "",
      category: null,
    },
    tempId: "",
    data: {
      date_range_week: {
        date: [1],
      },

      month_onday: {
        day: "",
      },

      month_onthe: {
        weekofmonth: "",
        dayofweek: "",
      },

      year_on: {
        month: "",
        day: "",
      },

      year_onthe: {
        dayofweek: "",
        date: "",
        month: "",
      },
      template_name: "",
      custom_date: 0,
      template_id: "",
      start_date: "",
      end_date: "",
      period: "",
      is_end: "",

      drop_down_list: 2,
      custom_dates: [],
      custom_dates_view: [],
      date_range_radio: 6,
      date_range_month_radio: 4,
      status: "",
      shedule_types: "",
      update_date: "",
    },
    isLoading: false,
    search: "",

    sheduled_types: "",

    selectedStatus: -1,
    selectedSchedule: -1,

    effective_date: "",
    alert_radio: "",

    scheduleOpen: false,
    isScheduleUpdateOpen: false,

    isStoreOpen: false,

    isStatusOpen: false,
    isNameOpen: false,

    isDeleteOpen: false,

    filters: {
      storefilter: [],
      all: [],
    },
    filterApplied: {},
    result: [],
    categoryBasedOtherRecipes: isList ? 0 : 1,
  });

  const moduleName = "templates";

  // App context
  const {
    setTableState,
    tableData,
    clearAllFilters,
    setSorting,
    setPagination,
    setSearchState,
  } = useContext(AppContext);
  const { sorting, pagination, search } = tableData[moduleName];

  const [value, setValue] = useState(0);
  const [debouncedText] = useDebounce(search, 1000);

  // Template Query
  const {
    listQuery,
    deleteTemplate,
    templateDetails,
    templateProductionDaysDetails,
    createScedule,
    dropdownListQuery,
    updateTemplateStatus,
    updateSceduler,
    categorylistQuery,
    recipelistQuery,
    generateBatch,
    state,
    reGenerateBatch,
    copyBatch,
    updateTemplateName,
    setState,
    createTemplateName,
    handleAddCustomDays,
    deleteRecurringDate,
    templateProductionDaysList,
    updateBatchSplit,
  } = useTemplateQueries({
    list,
    debouncedText,
    pagination: pagination,
    status: tableData?.templates?.data?.selectedStatus,
    schedule: tableData?.templates?.data?.selectedSchedule,
    sorting,
    resetDeleteInfo,
    details_id: templates.isDlt
      ? null
      : templates.tempId
        ? templates.tempId
        : details_id,
    isCategoryBasedOtherRecipes: templates.categoryBasedOtherRecipes,
    tempId: templates.tempId,
    updateId,
    dropdownlist,
    filterApplied: tableData?.templates?.data?.storefilter,
    setTemplates,
    templates,
    isStoreAdmin,
    selectedDate: templates?.selectedDate,
    isList,
    deleteInfo,
    selectedIndex,
    pc_temp_id: Number(templates?.pc_temp_id)
      ? Number(templates?.pc_temp_id)
      : Number(details_id),
  });

  const hasAnyBatchError = (templates) => {
    // 1. Any batch in any product has hasError === true
    const anyBatchHasError = templates?.batchDetails?.some((batch) =>
      (batch.products || []).some((product) =>
        (product.batches || []).some((b) => b?.hasError === true),
      ),
    );

    return anyBatchHasError;
  };

  // 2. Check if any batch column has all zero values across all products
  const hasLastBatchWeightZero = ({ templates, id, type }) => {
    let anyBatchColumnAllZero = false;

    for (const recipe of templates.batchDetails || []) {
      const products = recipe.products || [];
      if (products.length === 0) continue;

      const batchCount = products[0]?.batches?.length || 0;

      for (let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
        const isThisBatchAllZero = products.every((product) => {
          const batches = product.batches || [];
          if (batchIndex >= batches.length) return true; // treat missing as 0
          return (batches[batchIndex]?.units || 0) === 0;
        });

        if (isThisBatchAllZero) {
          anyBatchColumnAllZero = true;
          break;
        }
      }

      if (anyBatchColumnAllZero) break;
    }

    const allLastBatchWeightZero = anyBatchColumnAllZero;

    setTemplates((draft) => {
      draft.allLastBatchWeightZero = allLastBatchWeightZero;
    });

    if (allLastBatchWeightZero === false) {
      if (id) {
        onUpdate("secondary");
      } else {
        onSave("proceed");
      }
    }
  };

  // Store list query
  const { listQuery: storeLists } = useStoreSelectQueries({
    storeList: true && !isStoreAdmin,
  });

  const allDates = templateProductionDaysDetails?.data?.[0]?.added_dates ?? [];
  const [selectedDates, setSelectedDates] = useState(allDates);

  const allProductionDates =
    templateProductionDaysList?.data?.data?.map((item) => item.date) ?? [];

  const [selectedProductionDates, setSelectedProductionDates] = useState([]);
  const [unselectedProductionDates, setUnselectedProductionDates] = useState(
    [],
  );

  const handleBack = () => {
    setTemplates((draft) => {
      draft.filter.search = "";
      draft.batch_template_id = null;
    });
    if (templates.isCreateStoreAssign) {
      setTemplates((draft) => {
        draft.isCreateDateAssign = true;
        draft.isCreateStoreAssign = false;
        draft.data.custom_dates_view = templates.data.custom_dates;
      });
    } else if (templates.isCreateDateAssign) {
      setTemplates((draft) => {
        draft.isCreateDateAssign = false;
      });
      setState((draft) => {
        draft.isCreate = true;
      });
    } else {
      setState((draft) => {
        draft.isCreate = false;
      });
    }
  };

  const StoreValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "storefilter",
      value: item,
    });
    setTemplates((draft) => {
      draft.filters.status = "";
    });
  };

  const onChangeCategoryHandler = (value) => {
    setTemplates((draft) => {
      draft.chipCategoryId = draft.chipCategoryId === value ? 0 : value;
    });
  };

  const selectChipCategory = useCategorySelectChip({
    onChangeCategoryHandler,
  });

  const onChangeStateHandler = (value) => {
    setTemplates((draft) => {
      draft.selectedStore = value;
    });
  };

  const selectStore = useStoreSelect({ storelist: true, onChangeStateHandler });

  const onChangeStatusHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedStatus",
      value: value,
    });
  };

  const selectStatus = useStatusSelect({ onChangeStatusHandler });

  const onChangeSceduleHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedSchedule",
      value: value,
    });
  };

  const selectSchedule = useSceduleSelect({ onChangeSceduleHandler });

  const onSearchHandler = (search) => {
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1, // Reset to the first page
      }),
    });

    setSearchState({
      tableName: moduleName,
      key: "search",
      value: search,
    });
  };

  const clearSearch = () => {
    setTemplates((draft) => {
      draft.search = "";
    });
  };

  const onClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };

  const disableclearButton =
    (tableData.templates.data.selectedStatus === null ||
      tableData.templates.data.selectedStatus === "") &&
    (tableData.templates.data.selectedSchedule === null ||
      tableData.templates.data.selectedSchedule === "") &&
    Array.isArray(tableData.templates.data.storefilter) &&
    tableData.templates.data.storefilter.length === 0
      ? true
      : false;

  const handleDeleteModal = (type, data) => {
    setTemplates((draft) => {
      draft.isOpen = type;
      draft.id = data;
    });
  };

  // status change
  const handleClickOpen = (data, id) => {
    setTemplates((draft) => {
      draft.isStatusOpen = data;
      if (data === true) {
        draft.id = id;
      } else {
        draft.id = null;
        draft.alert_radio = "";
        draft.effective_date = "";
      }
    });
    validator.current.hideMessages();
  };

  //schedule update
  const handleClickUpdateOpen = (data, id, isCreateFlow) => {
    setTemplates((draft) => {
      draft.isScheduleUpdateOpen = data;
      if (data === true) {
        draft.id = id;
        draft.isCreateFlow = isCreateFlow;
      } else {
        draft.alert_radio = "1";
      }
    });
    validator.current.hideMessages();
  };

  // store update
  const handleAlertClickOpen = (data, id) => {
    setTemplates((draft) => {
      draft.isStoreOpen = data;
      if (data === true) {
        draft.id = id;
      } else {
        draft.id = null;
        draft.alert_radio = "";
      }
    });
    validator.current.hideMessages();
  };

  //scedule save and update
  const handleScheduleOpen = (data, id) => {
    setTemplates((draft) => {
      draft.scheduleOpen = data;
      if (data === true) {
        draft.id = id;
      }
    });
    validator.current.hideMessages();
  };

  const handleRemoveRecurringDate = (type) => {
    setTemplates((draft) => {
      draft.isRecurringDialog = type;
    });
  };

  //template name
  const handleNameOpen = (data, id) => {
    setTemplates((draft) => {
      draft.isNameOpen = data;
      if (data === true) {
        draft.id = id;
      } else {
        draft.id = null;
      }
    });
    validator.current.hideMessages();
  };

  const confirmDelete = () => {
    setTemplates((draft) => {
      draft.tempId = null;
      draft.isDlt = true;
    });
    const data = {
      custom_dates: [...unselectedProductionDates],
    };
    if (deleteInfo?.id) {
      deleteTemplate.mutate({ id: deleteInfo.id, isList, data });
    } else {
      deleteTemplate.mutate({ id: templates.id, isList, data });
      handleDeleteModal(false);
      navigateBasedOnUserType("/templates", isStoreAdmin, navigate);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setTemplates((draft) => {
      draft.data[name] = value;
    });
  };

  // function to construct recipe list
  const getRecipeList = (data) => {
    const filterList = data.filter((i) => i.condition !== 0) || [];
    return filterList.map((obj) => ({
      ...obj,
      wastage: obj?.wastage,
      quantity: 0,
      isChanged: false,
    }));
  };

  // Set recipe list
  useEffect(() => {
    if (recipelistQuery?.data) {
      const recipeList = getRecipeList(recipelistQuery.data);
      setTemplates((draft) => {
        draft.isList = true;
        draft.masterRecipeList = recipeList;
        draft.recipeList = recipeList;
      });
    }
  }, [recipelistQuery?.data]);

  // construct companied array
  useEffect(() => {
    // if (templateDetails?.data || templates.isList) {
    if (templateDetails?.data && templates.masterRecipeList.length > 0) {
      const data = templateDetails.data;

      // Set template status and name
      setTemplates((draft) => {
        draft.data.status = data?.status;
        draft.data.template_name = data?.template_name;
      });
      const array1 =
        getOtherRecipes(data?.other_recipes)?.map((item) => ({
          id: item.recipe_id,
          quantity: item.quantity,
          wastage: item.wastage,
          isChanged: item?.wastage > 0 || item?.quantity > 0 ? true : false,
        })) || [];

      const array2 =
        data?.batches?.map((item) => ({
          id: item.recipe_id,
          quantity: item.quantity,
          wastage: item.wastage,
          isChanged: item?.wastage > 0 || item?.quantity > 0 ? true : false,
        })) || [];

      // let combinedRecipes = [...array1, ...array2];
      const combinedRecipes = array2.concat(array1);

      const map = new Map(
        // templates?.recipeList?.map((item) => [item.id, item])
        templates.masterRecipeList?.map((item) => [item.id, item]),
      );

      // Update the map with array2 data
      combinedRecipes.forEach((item) => {
        if (map.has(item.id)) {
          map.set(item.id, { ...map.get(item.id), ...item });
        }
      });

      const combinedArray = Array.from(map.values());

      setTemplates((draft) => {
        draft.recipeList = combinedArray;
        draft.combinedList = combinedArray;
      });
    }
  }, [
    templateDetails?.data,
    templates.isList,
    templateProductionDaysDetails?.data,
  ]);

  // Set Template details
  useEffect(() => {
    if (templateProductionDaysDetails?.data) {
      const {
        custom_date,
        start_date,
        end_date,
        period,
        drop_down_list,
        added_dates,
        is_end,
        sheduled_types,
        date,
        day,
        month,
        weekofmonth,
        dayofweek,
      } = templateProductionDaysDetails?.data[0];
      setTemplates((draft) => {
        draft.isLoading = false;
        draft.data = {
          ...templateProductionDaysDetails?.data,
          custom_date: custom_date,
          start_date: start_date,
          end_date: end_date,
          period: period,
          drop_down_list: drop_down_list,
          custom_dates: added_dates,
          custom_dates_view: added_dates,
          is_end: is_end,
          shedule_types: sheduled_types,
          date_range_month_radio: sheduled_types,
          date_range_radio: sheduled_types,
        };
        draft.data.date_range_week = {
          date: date,
        };
        draft.data.month_onday = {
          day: day,
        };
        draft.data.month_onthe = {
          weekofmonth: weekofmonth,
          dayofweek: dayofweek,
        };
        draft.data.year_on = {
          month: month,
          day: day,
        };
        draft.data.year_onthe = {
          dayofweek: dayofweek,
          date: parseInt(date),
          month: month,
        };
      });
    }
  }, [templateProductionDaysDetails?.data, details_id]);

  const onDeleteTagSelect = (data, type) => {
    setTemplates((draft) => {
      draft.selectedDate = data;
      draft.isDeleteDialogOpen = type;
      draft.data.custom_dates = templates.data.custom_dates.filter((value) => {
        return value !== data;
      });
    });
  };

  const onChangeMultipleDate = (selectedValues) => {
    setTemplates((draft) => {
      draft.isDateSaveButton = selectedValues.length ? false : true;
    });
    const formatted = selectedValues?.map((date, index) => date?.format());
    setTemplates((draft) => {
      draft.data.custom_dates = formatted;
      draft.data.custom_dates_view = formatted;
    });
  };

  const onChangeDate = (name, selectedDate) => {
    const formattedDate = selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : null;
    setTemplates((draft) => {
      draft.data[name] = formattedDate;
    });
  };

  const onChangeEffectiveDate = (selectedDate) => {
    const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
    setTemplates((draft) => {
      draft.effective_date = formattedDate
        ? formattedDate
        : moment().format("YYYY-MM-DD");
    });
  };

  const RadioButtonChange = (value, name) => {
    const customDateValue = parseInt(value, 10);
    setTemplates((draft) => {
      draft.data[name] = customDateValue;
    });
  };

  const onChangeSceduler = (e) => {
    const { value } = e.target;
    setTemplates((draft) => {
      draft.data.month_onday.day = value;
    });
  };

  const onChangeYear = (e) => {
    const { value } = e.target;
    setTemplates((draft) => {
      draft.data.year_on.day = value;
    });
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setTemplates((draft) => {
      draft.data[name] = value;
    });
  };

  // ++++++++++++++++++++++++++++++++++++++++ Days ++++++++++++++++++++++++++++++++++++++++
  const handleRepeatOptionChange = (event, option) => {
    const stateOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.drop_down_list = stateOption;
    });
  };

  //++++++++++++++++++++++++++++++++++++++++ month on the ++++++++++++++++++++++++++++++++++++++++
  const weekOfMonthOptionChange = (event, option) => {
    const WeekOfMonthOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.month_onthe.weekofmonth = WeekOfMonthOption;
    });
  };

  const weekDaysOptionChange = (event, option) => {
    const weekOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.month_onthe.dayofweek = weekOption;
    });
  };

  // ++++++++++++++++++++++++++++++++++++++++ year on ++++++++++++++++++++++++++++++++++++++++
  const monthOptionChange = (event, option) => {
    const monthOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.year_on.month = monthOption;
    });
  };

  // ++++++++++++++++++++++++++++++++++++++++ year on the ++++++++++++++++++++++++++++++++++++++++
  const DaysOfWeekOptionChange = (event, option) => {
    const daysOfWeekOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.year_onthe.dayofweek = daysOfWeekOption;
    });
  };

  const WeekDaysOptionChange = (event, option) => {
    const weekDaysOption = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.year_onthe.date = weekDaysOption;
    });
  };

  const OptionChange = (event, option) => {
    const optionValue = option ? option.value : "";
    setTemplates((draft) => {
      draft.data.year_onthe.month = optionValue;
    });
  };

  const detailsStatusChangeHandler = (event, option) => {
    const statusOpt = option ? option.id : "";
    setTemplates((draft) => {
      draft.data.status = statusOpt;
    });
  };

  const handleDayClick = (dayValue) => {
    setTemplates((draft) => {
      if (!Array.isArray(draft.data.date_range_week.date)) {
        draft.data.date_range_week.date = [];
      }
      const index = draft.data.date_range_week.date.indexOf(dayValue);
      if (index === -1) {
        draft.data.date_range_week.date.push(dayValue);
      } else {
        draft.data.date_range_week.date.splice(index, 1);
      }
    });
  };

  const statusChangeHandler = (event, option) => {
    detailsStatusChangeHandler(event, option);
  };

  const handleDeleteScheduledDatesDialog = (
    open,
    isScheduled,
    event,
    option,
  ) => {
    const hasNoProductionDays =
      templateProductionDaysList?.data?.data?.length < 1;

    if (templateDetails?.data?.status === 0) {
      if (!hasNoProductionDays) {
        handleClickOpen(true, templateDetails?.data?.template_id);
      } else {
        onSubmitStatus(event, option);
        setTemplates((draft) => {
          draft.isDeleteScheduledDatesDialogOpen = false;
        });
      }
    }

    if (isScheduled?.template_scheduled_status === 1) {
      if (templateDetails?.data?.status === 1) {
        if (!hasNoProductionDays) {
          setTemplates((draft) => {
            draft.isDeleteScheduledDatesDialogOpen = open;
          });
        } else {
          setTemplates((draft) => {
            draft.isDeleteScheduledDatesDialogOpen = open;
          });
        }
      }
    } else {
      if (templateDetails?.data?.status === 1) {
        onSubmitStatus(event, option);
        setTemplates((draft) => {
          draft.isDeleteScheduledDatesDialogOpen = false;
        });
      }
    }
  };

  const onSubmitStatus = (event, option) => {
    setTemplates((draft) => {
      draft.isLoading = true;
      draft.isDeleteScheduledDatesDialogOpen = false;
    });

    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate((prev) => prev + 1);
      return;
    }

    const payload =
      templates?.data?.status === 1
        ? {
            status: 0,
            custom_dates: [...unselectedProductionDates],
          }
        : {
            status: 1,
            effective_date: templates?.effective_date,
          };

    updateTemplateStatus.mutate({
      details_id,
      payload,
      handleClickOpen,
      setTemplates,
    });
  };

  const RemoveButtonHandeler = () => {
    setTemplates((draft) => {
      draft.data.end_date = null;
    });
  };

  // If the template is scheduled, the recipe list gets unnecessarily reassigned after it has already been correctly set by the previous useEffect.
  // useEffect(() => {
  //   if (templateProductionDaysDetails?.data) {
  //     setTemplates((draft) => {
  //       draft.isLoading = false;
  //       draft.data.status = templateDetails?.data?.status;
  //     });

  //     if (templates?.tempId) {
  //       const array1 = getOtherRecipes(
  //         templateDetails?.data?.other_recipes,
  //       )?.map((item) => ({
  //         id: item.recipe_id,
  //         quantity: item.quantity,
  //         wastage: item.wastage,
  //         isChanged: item.wastage > 0 || item.quantity > 0 ? true : false,
  //       }));

  //       const array2 = templateDetails?.data?.batches?.map((item) => ({
  //         id: item.recipe_id,
  //         quantity: item.quantity,
  //         wastage: item.wastage,
  //         isChanged: item.wastage > 0 || item.quantity > 0 ? true : false,
  //       }));

  //       if (array1 && array2) {
  //         // let combinedRecipes = [...array1, ...array2];
  //         let combinedRecipes = array2.concat(array1);

  //         setTemplates((draft) => {
  //           draft.recipeList = [...draft.recipeList, ...combinedRecipes];
  //           draft.data.template_name = templateDetails?.data?.template_name;
  //         });
  //         combinedRecipes.sort((a, b) => a.id - b.id);

  //         let b = templates.recipeList.map((data) => {
  //           if (data?.id === combinedRecipes?.at(-1)?.id) {
  //             const val = { ...data, ...combinedRecipes.at(-1) };
  //             combinedRecipes.pop();
  //             return val;
  //           } else return data;
  //         });

  //         setTemplates((draft) => {
  //           draft.recipeList = b;
  //         });
  //       }
  //     }
  //   }
  // }, [
  //   templateDetails?.data,
  //   templates.tempId,
  //   templates.filter.search,
  //   templates.isOpen,
  //   templateProductionDaysDetails?.data,
  // ]);

  const onSubmit = () => {
    setTemplates((draft) => {
      draft.isLoading = true;
    });
    if (validator.current.allValid()) {
      const {
        custom_date,
        start_date,
        end_date,
        period,
        date_range_week,
        month_onday,
        month_onthe,
        year_on,
        year_onthe,
        date_range_radio,
        date_range_month_radio,
        drop_down_list,
      } = templates?.data;

      const { effective_date } = templates;
      if (drop_down_list === 1) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          effective_date,
          shedule_types: 2,
        };

        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      } else if (drop_down_list === 2) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          date: date_range_week.date,
          effective_date,
          shedule_types: 3,
        };
        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      } else if (drop_down_list === 3 && date_range_month_radio === 4) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          day: month_onday.day,
          effective_date,
          shedule_types: 4,
        };

        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      } else if (drop_down_list === 3 && date_range_month_radio === 5) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          weekofmonth: month_onthe.weekofmonth,
          dayofweek: month_onthe.dayofweek,
          effective_date,
          shedule_types: 5,
        };

        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      } else if (drop_down_list === 4 && date_range_radio === 6) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          month: year_on.month,
          day: year_on.day,
          effective_date,
          shedule_types: 6,
        };

        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      } else if (drop_down_list === 4 && date_range_radio === 7) {
        const isEnd = end_date ? 1 : 0;
        const payload = {
          custom_date,
          template_id: templateDetails?.data?.template_id
            ? templateDetails?.data?.template_id
            : state?.templateDetails?.data?.template_id,
          start_date,
          ...(end_date && { end_date }),
          is_end: isEnd,
          period: Number(period),
          dayofweek: year_onthe?.dayofweek,
          date: year_onthe?.date,
          month: year_onthe?.month,
          effective_date,
          shedule_types: 7,
        };
        updateSceduler.mutate({
          data: payload,
          handleClickUpdateOpen,
          handleScheduleOpen,
          setTemplates,
        });
      }
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  const onNameCreateSubmit = () => {
    setTemplates((draft) => {
      draft.isLoading = true;
    });

    const { template_name } = templates.data;
    const payload = {
      template_name,
      template_id: state?.templateDetails?.data?.template_id,
    };
    createTemplateName.mutate({
      payload,
      handleOpenClick,
      handleOpenClickDetailCopy,
    });
  };

  const onNameSubmit = () => {
    setTemplates((draft) => {
      draft.isLoading = true;
    });
    if (validator.current.allValid()) {
      const { template_name } = templates.data;
      const payload = {
        template_name,
      };
      updateTemplateName.mutate({
        details_id,
        payload,
        handleNameOpen,
        setTemplates,
      });
    } else {
      validator.current.showMessages();
      forceUpdate(1);
      setTemplates((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleRecipes = async () => {
    try {
      const recipes = [];

      const filteredRecipes = templates.recipeList?.filter(
        (i) => i.category_id === templates?.chipCategoryId,
      );
      filteredRecipes?.forEach((item) => {
        if (!recipes.find((ri) => ri.id === item.subcategory_id)) {
          recipes.push({
            id: item.subcategory_id,
            recipe_name: item.subcategory_name,
            subRows: [],
          });
        }
      });
      if (recipes) {
        filteredRecipes?.forEach((item) => {
          var findRecipe = recipes.find((ri) => ri.id === item.subcategory_id);
          if (findRecipe) {
            findRecipe.subRows.push({
              id: item.id,
              recipe_name: item.name,
              status: item.status,
              store: item.stores,
              type: item.type,
            });
          }
        });
      }
      return recipes;
    } catch (err) {
      return [];
    }
  };

  // Set Tab change
  useEffect(() => {
    if (
      (state.isCreate && state?.templateDetails?.data) ||
      templates.isTabChange
    ) {
      handleOptionChangeRecipeBatch(
        state?.templateDetails?.data?.batches[0],
        0,
      );
      setTemplates((draft) => {
        draft.isTabChange = false;
      });
    }
  }, [state.isCreate, state?.templateDetails?.data, templates.isTabChange]);

  // Set custom table change
  useEffect(() => {
    handleCustomTableChange();
  }, [templates?.recipeList]);

  const handleCustomTableChange = (data) => {
    if (templates?.recipeList) {
      const groupByCategory = templates.recipeList.reduce((result, product) => {
        if (!result[product.category_id]) {
          result[product.category_id] = {
            category_id: product.category_id,
            category_name: product.category_name,
            subRows: [],
          };
        }
        result[product.category_id].subRows.push(product);
        setTemplates((draft) => {
          draft.groupedDatas = result;
        });
        return result;
      }, {});
    }
  };

  const onChangeFilter = (e, data) => {
    if (e?.id && data?.[e?.id]?.subRows?.length) {
      const groupedBySubcategory = Object.values(
        data[e?.id]?.subRows?.reduce((acc, item) => {
          const key = item["subcategory_id"];
          if (!acc[key]) {
            acc[key] = {
              category_id: item["category_id"],
              subcategory_name: item["subcategory_name"],
              subcategory_display_order: item["subcategory_display_order"],
              subRows: [],
            };
          }
          acc[key].subRows.push({
            ...item,
            quantity: item.quantity ?? 0,
            wastage: item.wastage ?? 0,
          });
          return acc;
        }, {}),
      ).sort(
        (a, b) => a.subcategory_display_order - b.subcategory_display_order,
      );
      setTemplates((draft) => {
        draft.filter.search = "";
        draft.filter.category = e.id;
        draft.filter.selectedCategory = e;
        draft.result = groupedBySubcategory;
        draft.templateResultRows = groupedBySubcategory;
      });
    } else {
      setTemplates((draft) => {
        draft.filter.category = e?.id;
        draft.filter.selectedCategory = e;
        draft.result = [];
        draft.templateResultRows = [];
      });
    }
  };

  const afterOnSave = () => {
    setTemplates((draft) => {
      draft.isChanged = false;
    });
  };

  const filterRecipeList = templates.recipeList.filter((i) => i.quantity > 0);

  const filterRecipeListValue = filterRecipeList?.map((item) => ({
    recipe_id: item.id,
    quantity: item.quantity,
    wastage: item.wastage,
  }));

  const batchFilter1 = filterRecipeListValue?.filter((i) => i.quantity !== 0);

  const onUpdate = (type) => {
    const filterRecipeList = templates.recipeList.filter((i) => i.quantity > 0);

    const filterRecipeListValue = filterRecipeList?.map((item) => ({
      recipe_id: item.id,
      quantity: Number(item.quantity),
      wastage: item.wastage ? Number(item.wastage) : 0,
    }));

    const batchFilter1 = filterRecipeListValue?.filter((i) => i.quantity !== 0);
    let payload = {};
    if (type === "initial") {
      payload = {
        batches: [...batchFilter1],
        edit_status: 0,
      };
    } else if (type === "secondary") {
      payload = {
        batches: [...batchFilter1],
        edit_status: 1,
      };
    } else {
      payload = {
        batches: [...batchFilter1],
        edit_status: 2,
        effective_date: templates.effective_date,
      };
    }
    reGenerateBatch.mutate({ data: payload, handleRegenerateOpenClick, type });
  };

  const onSave = (type) => {
    const filterRecipeList = templates.recipeList.filter((i) => i.quantity > 0);
    const filterRecipeListValue = filterRecipeList?.map((item) => ({
      recipe_id: item.id,
      quantity: item.quantity && Number(item.quantity),
      wastage: item.wastage ? Number(item.wastage) : 0,
    }));
    const batchFilter1 = filterRecipeListValue?.filter((i) => i.quantity !== 0);

    let payload = {};
    if (type === "initial") {
      payload = {
        batches: batchFilter1,
        edit_status: 0,
      };
    } else if (type === "proceed") {
      payload = {
        batches: batchFilter1,
        edit_status: 1,
      };
    }
    if (templates.tempId) {
      generateBatch.mutate({
        data: payload,
        afterOnSave,
        id: templates.batch_template_id,
        type,
      });
      setTemplates((draft) => {
        draft.isTrue = true;
      });
    } else {
      generateBatch.mutate({
        data: payload,
        afterOnSave,
        id: templates.batch_template_id,
        type,
      });
    }
  };

  const handleOpenClickDetailCopy = async (type, id) => {
    if (type) {
      setTemplates((draft) => {
        draft.batch_template_id = null;
        draft.isfilterData = false;
        draft.isOpenDialog = type;
        draft.tempId = id;
        draft.isCreate = false;
        draft.isCreateDateAssign = false;
        draft.isCreateStoreAssign = false;
        draft.data.custom_dates_view = [];
        draft.data.custom_dates = [];
        draft.categoryBasedOtherRecipes = 0;
      });
    } else {
      setState((draft) => {
        draft.isCreate = false;
      });
      setTemplates((draft) => {
        draft.isOpenDialog = type;
        draft.batch_template_id = null;
        draft.categoryBasedOtherRecipes = 1;
        draft.filter.category = null;
        draft.data.template_name = "";
        draft.data.status = "";
      });
    }
  };

  // Helper to merge recipes from templateDetails
  const buildCombinedList = (templateDetails, prevRecipeList = []) => {
    const array1 =
      getOtherRecipes(templateDetails?.data?.other_recipes)?.map((item) => ({
        id: item.recipe_id,
        quantity: item.quantity,
        wastage: item.wastage,
        isChanged: item?.wastage > 0 || item?.quantity > 0 ? true : false,
      })) || [];
    const array2 =
      templateDetails?.data?.batches?.map((item) => ({
        id: item.recipe_id,
        quantity: item.quantity,
        wastage: item.wastage,
        isChanged: item?.wastage > 0 || item?.quantity > 0 ? true : false,
      })) || [];

    // let combinedRecipes = [...array1, ...array2];
    let combinedRecipes = array2.concat(array1);

    const map = new Map(prevRecipeList?.map((item) => [item.id, item]));

    combinedRecipes.forEach((item) => {
      if (map.has(item.id)) {
        map.set(item.id, { ...map.get(item.id), ...item });
      } else {
        map.set(item.id, item);
      }
    });
    return Array.from(map.values());
  };

  const handleOpenClick = async (type, id) => {
    setSelectedIndex(0);
    // Always rebuild combinedList from latest templateDetails if opening modal
    // let latestCombinedList = templates.combinedList;
    let latestCombinedList = templates.masterRecipeList;

    // works on regenerate from template details
    if (type && templateDetails?.data) {
      latestCombinedList = buildCombinedList(
        templateDetails,
        templates.recipeList,
      );
      setTemplates((draft) => {
        draft.combinedList = latestCombinedList;
      });
    }
    console.log("latestCombinedList: ", latestCombinedList, "tempId: ", id);

    setTemplates((draft) => {
      draft.recipeList = latestCombinedList;
      draft.tempId = id;
      draft.isOpen = type;
      draft.filter.search = "";
      draft.isfilterData = false;
      draft.isCreateDialog = false;
      draft.batch_template_id = null;
      draft.isShowErrorMessage = false;
      draft.categoryBasedOtherRecipes = 0;
    });

    if (type === false) {
      setState((draft) => {
        draft.isCreate = false;
      });
      setTemplates((draft) => {
        // draft.isShowErrorMessage = false;
        draft.filter.category = null;
        draft.categoryBasedOtherRecipes = 1;
        draft.data.template_name = "";
        draft.data.status = "";
      });
    }
  };

  const handleRegenerateOpenClick = async (type, id) => {
    setTemplates((draft) => {
      draft.isRegenerateOpen = type;
    });
  };

  // Update template details from generate batch
  useEffect(() => {
    if (generateBatch) {
      setTemplates((draft) => {
        draft.templateDetails = generateBatch.variable;
      });
    }
  }, [generateBatch]);

  const handleOptionChangeRecipe = async (item, index) => {
    setTemplates((draft) => {
      draft.templateItems = item;
      draft.indexColumn = index;
    });
  };

  useEffect(() => {
    if (
      (state.isCreate && state?.templateDetails?.data) ||
      templates.isTabChange
    ) {
      const otherRecipes = getOtherRecipes(
        state?.templateDetails?.data?.other_recipes,
      );
      handleOptionChangeRecipe(otherRecipes[0], 0);
      setTemplates((draft) => {
        draft.isTabChange = false;
      });
    }
  }, [state.isCreate, state?.templateDetails?.data, templates.isTabChange]);

  const handleOptionChangeRecipeBatch = async (item, index) => {
    setTemplates((draft) => {
      draft.templateBatchItems = item;
      draft.indexColumn = index;
    });
  };

  // handle option change on create
  useEffect(() => {
    if (state.isCreate) {
      handleOptionChangeRecipeBatch(
        state?.templateDetails?.data?.batches[0],
        0,
      );
    }
  }, [state.isCreate, state?.templateDetails?.data]);

  const optionHandleChangeBatches = async (item) => {
    setTemplates((draft) => {
      draft.batchesItems = item;
    });
  };

  const optionHandleChangeRecipies = async (item) => {
    setTemplates((draft) => {
      draft.recipeItems = item;
    });
  };

  // Update template id and status
  useEffect(() => {
    if (templates.isTrue) {
      setTemplates((draft) => {
        draft.tempId = state?.templateDetails?.data?.template_id;
        draft.data.status = templateDetails?.data?.status;
      });
    }
  }, [templates.isTrue, state.templateDetails]);

  // optimized search function for create/copy template dialog search
  const searchFn = (value, data = []) => {
    if (!value || !Array.isArray(data))
      return {
        totalCount: 0,
        result: data,
      };

    let totalTemplateSearchResult = 0;
    const searchValue = value.toLowerCase();
    const subcategoryMap = {};

    for (let i = 0; i < data.length; i++) {
      const category = data[i];
      const subRows = category.subRows || [];

      for (let j = 0; j < subRows.length; j++) {
        const item = subRows[j];
        const name = item.name;

        if (name && name.toLowerCase().includes(searchValue)) {
          const subId = item.subcategory_id;

          if (!subcategoryMap[subId]) {
            subcategoryMap[subId] = {
              category_id: item.category_id,
              category_name: item.category_name,
              subcategory_id: item.subcategory_id,
              subcategory_name: item.subcategory_name,
              subcategory_display_order: item.subcategory_display_order,
              subRows: [],
            };
          }

          subcategoryMap[subId].subRows.push(item);
          totalTemplateSearchResult++;
        }
      }
    }
    return {
      totalCount: totalTemplateSearchResult,
      result: Object.values(subcategoryMap).sort(
        (a, b) => a.subcategory_display_order - b.subcategory_display_order,
      ),
    };

    // return data
    //   .map((category) => {
    //     const filteredSubRows = category?.subRows?.filter((subRow) => {
    //       return subRow.name?.toLowerCase().includes(searchValue);
    //     });

    //     return filteredSubRows.length > 0
    //       ? { ...category, subRows: filteredSubRows }
    //       : null;
    //   })
    //   .filter(Boolean)
    //   .sort(
    //     (a, b) => a.subcategory_display_order - b.subcategory_display_order,
    //   );
  };

  const searchCallback = useDebouncedCallback((value, allData) => {
    let totalResult = 0;
    let templateResultRows = templates.result;
    if (value) {
      const { totalCount, result } = searchFn(
        value,
        Object.values(allData) || templates.result,
      );

      templateResultRows = result;
      totalResult = totalCount;
    }

    setTemplates((draft) => {
      draft.filter.search = value;
      draft.templateResultRows = templateResultRows;
      draft.totalTemplateSearchResult = totalResult;
    });
  }, 1000);

  const onSearchChange = (value, allData) => {
    setIsSearching(!!value); // true if searching, false if cleared
    searchCallback(value, allData);
  };

  const searchHandler = (value) => {
    setIsSearching(!!value);
    // onSearchChange(value);
    setTemplates((draft) => {
      draft.filter.search = value;
      draft.templateResultRows = templates.result;
      draft.totalTemplateSearchResult = 0;
    });
  };

  // Effect to handle initial filter application when the component opens
  useEffect(() => {
    if (
      !isSearching && // <-- Only run if not searching
      templates.isOpen &&
      categorylistQuery?.data &&
      templates?.filter?.category
    ) {
      const filterData = categorylistQuery?.data?.find(
        (item) => item.id === templates.filter.category,
      );

      onChangeFilter(
        templates?.filter?.category ? filterData : categorylistQuery?.data[0],
        templates?.groupedDatas,
      );

      setTemplates((draft) => {
        draft.isInitialLoad = true;
      });
    }
  }, [
    isSearching, // <-- Add as dependency
    templates.isOpen,
    templates.filter.category,
    categorylistQuery?.data,
    state.isCreate,
  ]);

  // Effect to update templates only when groupedDatas is initially passed (not on every change)
  useEffect(() => {
    if (templates.groupedDatas && !templates.isfilterData && !isSearching) {
      onChangeFilter(
        templates?.filter?.category
          ? categorylistQuery?.data?.find(
              (item) => item.id === templates.filter.category,
            )
          : categorylistQuery?.data?.[0],
        templates.groupedDatas,
      );
    }
  }, [templates.groupedDatas, templates.isfilterData, isSearching]); // This will run only when groupedDatas is initially set

  const onSaveCustomDays = () => {
    const payload = {
      custom_dates: templates?.data?.custom_dates,
      template_id: details_id
        ? details_id
        : state?.templateDetails?.data?.template_id,
    };
    handleAddCustomDays.mutate({ data: payload, onDeleteTagSelect });
  };

  const handleCustomFieldChange = (id, e, type) => {
    const { value } = e.target;
    setTemplates((draft) => {
      draft.isfilterData = true;
      draft.result.forEach((category) => {
        const subRowItem = category.subRows.find((item) => item.id === id);
        if (subRowItem) {
          subRowItem[type] = value;
          subRowItem.isChanged = true;
        }
      });
      draft.templateResultRows.forEach((category) => {
        const subRowItem = category.subRows.find((item) => item.id === id);
        if (subRowItem) {
          subRowItem[type] = value;
          subRowItem.isChanged = true;
        }
      });

      draft.recipeList = draft.recipeList.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            [type]: value,
            isChanged: true,
          };
        }
        return item;
      });
    });
  };

  const handleClose = () => {
    setState((draft) => {
      draft.isErrorMessage = false;
    });
  };

  const confirmDeleteRecurringDates = () => {
    deleteRecurringDate.mutate({ id: details_id, handleRemoveRecurringDate });
  };

  const handleBatchClick = (item, index) => {
    setTemplates((draft) => {
      draft.indexColumn = index;
      draft.selectedBatch = item;
    });
  };

  // Update batch section
  useEffect(() => {
    if (templateDetails?.data?.batches?.length > 0) {
      setTemplates((draft) => {
        draft.selectedBatch = templateDetails?.data?.batches[0];
        draft.indexColumn = 0;
      });
    } else {
      setTemplates((draft) => {
        draft.selectedBatch = null;
        draft.indexColumn = 0;
      });
    }
  }, [templateDetails?.data, value]);

  // Set a default recipe when template data changes
  useEffect(() => {
    if (templates.selectedRecipe?.recipe_id) return;
    // Fallback to empty array if other_recipes is undefined/null
    const otherRecipes = templateDetails?.data?.other_recipes || [];

    // Proceed only if at least one recipe group exists
    if (otherRecipes.length > 0) {
      // Check whether recipes are grouped by category (nested recipes array)
      const isCategoryBasedGrouping = Array.isArray(otherRecipes[0]?.recipes);

      // Pick the first recipe that supports chart rendering (show_in_chart)
      // Logic differs based on whether recipes are category-based or flat
      const selectedRecipe = isCategoryBasedGrouping
        ? (otherRecipes[0].recipes.find((recipe) => recipe.show_in_chart) ??
          null)
        : otherRecipes[0];

      // Update template state with the selected recipe
      // indexColumn is reset for consistency, but recipe selection is now driven by recipeId (not index-based selection)
      setTemplates((draft) => {
        draft.selectedRecipe = selectedRecipe;
        draft.indexColumn = 0;
      });
    }
  }, [templateDetails?.data?.other_recipes, value]);

  // Function to click recipe item
  const handleRecipeClick = (item, index) => {
    setTemplates((draft) => {
      draft.indexColumn = index;
      draft.selectedRecipe = item;
    });
  };

  const handleShowErrorMessage = (value) => {
    setTemplates((draft) => {
      draft.isShowErrorMessage = value;
    });
  };

  const handleStoreAssignDialog = (type, id) => {
    setTemplates((draft) => {
      draft.isStoreDialogOpen = type;
      draft.id = id;
    });
  };

  const handleCreateDateAssign = (e) => {
    const value = e.target.checked === true ? 1 : 0;
    setTemplates((draft) => {
      draft.is_create_date_assign = value;
    });
  };

  const handleNextButton = () => {
    setTemplates((draft) => {
      draft.isCreateDateAssign = false;
      draft.isCreateStoreAssign = true;
    });
  };

  // Update template schedule status and template id
  useEffect(() => {
    if (templateDetails.data) {
      setTemplates((draft) => {
        draft.template_scheduled_status =
          templateDetails.data.template_scheduled_status;
        draft.tempId = templateDetails.data.template_id;
      });
    }
  }, [templateDetails.data]);

  // Update template id and pc template id
  useEffect(() => {
    templateId &&
      setTemplates((draft) => {
        draft.tempId = templateId;
        draft.pc_temp_id = templateId;
      });
  }, [templateId]);

  //batch split section

  const handleBatchSplitUpdate = (productIndex, batchIndex) => {
    const sanitizedBatchDetails = sanitizeBatchDetails(templates?.batchDetails);
    updateBatchSplit.mutate({
      id: templates.batch_template_id
        ? templates.batch_template_id
        : templates.tempId,
      payload: {
        batches: sanitizedBatchDetails,
        type: "edit",
      },
    });
    setEditedMap((prev) => {
      const newMap = { ...prev };
      delete newMap[`${productIndex}-${batchIndex}`];
      return newMap;
    });
    setTemplates((draft) => {
      draft.batchIndex = batchIndex;
      draft.productIndex = productIndex;
    });
    setFocusedField(null); // Remove highlight
  };

  const handleAddBatch = () => {
    const batchDetails = templates?.batchDetails || [];

    const currentBatch = batchDetails[selectedIndex];

    // Update each product with a new batch item
    const updatedProducts = currentBatch.products.map((product) => ({
      ...product,
      batches: [...product.batches, { units: 0, weight: 0 }],
    }));

    // Merge back updated products into batch
    const updatedBatch = {
      ...currentBatch,
      products: updatedProducts,
      batch_count: currentBatch.batch_count + 1,
    };

    // Now, update the full batchDetails array
    const updatedBatchDetails = batchDetails.map((batch, index) =>
      index === selectedIndex ? updatedBatch : batch,
    );

    updateBatchSplit.mutate({
      id: templates.batch_template_id
        ? templates.batch_template_id
        : templates.tempId,
      payload: {
        batches: updatedBatchDetails,
        type: "add",
      },
    });
  };

  function removeAllZeroBatches(batchDetails) {
    if (!Array.isArray(batchDetails) || batchDetails.length === 0)
      return batchDetails;

    return batchDetails.map((batchGroup) => {
      const products = batchGroup.products || [];
      if (products.length === 0) return batchGroup;

      // Find the length of batches (assume all products in this group have the same number of batches)
      const batchLength = products[0].batches.length;

      // Find batch indexes where all products in this group have units and weight as 0
      const zeroBatchIndexes = [];
      for (let i = 0; i < batchLength; i++) {
        const allZero = products.every(
          (product) =>
            product.batches[i]?.units === 0 && product.batches[i]?.weight === 0,
        );
        if (allZero) zeroBatchIndexes.push(i);
      }

      // Remove those batch indexes from all products' batches arrays in this group
      const updatedProducts = products.map((product) => ({
        ...product,
        batches: product.batches.filter(
          (_, idx) => !zeroBatchIndexes.includes(idx),
        ),
      }));

      // Update batch_count for this group
      const updatedBatch = {
        ...batchGroup,
        products: updatedProducts,
        batch_count: updatedProducts[0]?.batches.length || 0,
      };

      return updatedBatch;
    });
  }

  const handleDeleteBatch = (type) => {
    const updatedProducts = templates?.batchDetails[
      selectedIndex
    ]?.products?.map((product) => {
      return {
        ...product,
        batches: product.batches.filter(
          (_, index) => index !== templates.batchIndex,
        ),
      };
    });
    const updatedBatch = {
      ...templates?.batchDetails[selectedIndex],
      products: updatedProducts,
      batch_count: templates?.batchDetails[selectedIndex].batch_count + 1,
    };
    const updatedBatchDetails = templates?.batchDetails.map((batch, index) =>
      index === selectedIndex ? updatedBatch : batch,
    );

    updateBatchSplit.mutate({
      id: templates.batch_template_id
        ? templates.batch_template_id
        : templates.tempId,
      payload: {
        batches:
          type === "all"
            ? removeAllZeroBatches(templates.batchDetails)
            : updatedBatchDetails,
        type: "delete",
      },
      handleDeleteBatchDialog,
    });
  };

  const handleDeleteBatchDialog = (type, batchIndex) => {
    setTemplates((draft) => {
      draft.isDeleteBatchDialogOpen = type;
      draft.batchIndex = batchIndex;
      draft.allLastBatchWeightZero = false;
    });
  };

  return {
    listQuery,
    templates,
    validator,
    clearSearch,
    pagination,
    setPagination,
    onSearchHandler,
    sorting,
    setSorting,
    selectStore,
    selectStatus,
    onClearFilter,
    disableclearButton,
    selectSchedule,
    handleClickOpen,
    confirmDelete,
    handleDeleteModal,
    onDelete,
    deleteInfo,
    resetDeleteInfo,
    templateDetails,
    templateProductionDaysDetails,
    createScedule,
    onSubmit,
    onDeleteTagSelect,
    onChangeDate,
    RadioButtonChange,
    onChangeSceduler,
    dropdownListQuery,
    handleRepeatOptionChange,
    weekOfMonthOptionChange,
    monthOptionChange,
    weekDaysOptionChange,
    onChangeYear,
    onChangeHandler,
    DaysOfWeekOptionChange,
    WeekDaysOptionChange,
    OptionChange,
    handleDayClick,
    detailsStatusChangeHandler,
    onSubmitStatus,
    updateTemplateStatus,
    handleAlertClickOpen,
    onChangeMultipleDate,
    handleClickUpdateOpen,
    setTemplates,
    RemoveButtonHandeler,
    statusChangeHandler,
    handleScheduleOpen,
    StoreValuesHandler,
    categorylistQuery,
    selectChipCategory,
    recipelistQuery,
    handleRecipes,
    onChangeFilter,
    onSave,
    onUpdate,
    handleOpenClick,
    state,
    handleOptionChangeRecipe,
    handleOptionChangeRecipeBatch,
    optionHandleChangeBatches,
    optionHandleChangeRecipies,
    onChangeEffectiveDate,
    handleNameOpen,
    onChange,
    onNameSubmit,
    handleRegenerateOpenClick,
    onSearchChange,
    filterRecipeList,
    handleBack,
    onDeleteDetails,
    onNameCreateSubmit,
    generateBatch,
    createTemplateName,
    copyBatch,
    reGenerateBatch,
    searchHandler,
    updateTemplateName,
    storeLists,
    batchFilter1,
    forceUpdate,
    onSubmits: onSubmit,
    onSaveCustomDays,
    handleAddCustomDays,
    isCreateVisible,
    isDeletable,
    isCopyTemplate,
    isStatusChange,
    isEditable,
    isCreateSchedule,
    isStoreAssign,
    handleOpenClickDetailCopy,
    globalData,
    handleCustomFieldChange,
    handleClose,
    handleRemoveRecurringDate,
    confirmDeleteRecurringDates,
    tableData,
    moduleName,
    search,
    handleBatchClick,
    handleRecipeClick,
    handleShowErrorMessage,
    value,
    setValue,
    setState,
    handleStoreAssignDialog,
    handleCreateDateAssign,
    handleNextButton,
    handleDeleteScheduledDatesDialog,
    selectedDates,
    setSelectedDates,
    allDates,
    allProductionDates,
    selectedProductionDates,
    setSelectedProductionDates,
    unselectedProductionDates,
    setUnselectedProductionDates,
    templateProductionDaysList,
    handleBatchSplitUpdate,
    selectedIndex,
    setSelectedIndex,
    setFocusedField,
    editedMap,
    focusedField,
    setEditedMap,
    handleAddBatch,
    handleDeleteBatch,
    handleDeleteBatchDialog,
    updateBatchSplit,
    hasAnyBatchError,
    hasLastBatchWeightZero,
  };
};
